import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { MILL_1, MILL_2 } from "@/setup/mill-config/mill-config";
import { useCalendarPermissions } from "@/shared/hooks/useCalendarPermissions";
import { masterDetailsMinHeight, pageBgColor } from "@/utils/theme";
import {
	ErrorModal,
	HelmetDexteel,
	TimeService,
	useContextMenuMESF,
	useUTLSettingsContext,
} from "@dexteel/mesf-core";
import FullCalendar from "@fullcalendar/react";
import {
	CalendarToday,
	Email,
	History,
	List as ListIcon,
	Publish,
	Restore,
	Save,
} from "@mui/icons-material";
import {
	alpha,
	Box,
	Button,
	ButtonGroup,
	Chip,
	Paper,
	Tooltip,
	Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { get } from "lodash-es";
import moment from "moment-timezone";
import { enqueueSnackbar } from "notistack";
import { type MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CustomEventsTable } from "./components/CustomEventsTable";
import { EnhancedMonthCalendar } from "./components/EnhancedMonthCalendar";
import { ConfirmDeleteEventModal } from "./components/modals/ConfirmDeleteEventModal";
import { ConfirmDraftEventsModal } from "./components/modals/ConfirmDraftEventsModal";
import { ConfirmEventModal } from "./components/modals/ConfirmEventModal";
import { EmailCalendarModal } from "./components/modals/EmailCalendarModal";
import { EventModal } from "./components/modals/EventModal";
import { NewEventModal } from "./components/modals/NewEventModal";
import { RestoreDraftSnapshotModal } from "./components/modals/RestoreDraftSnapshotModal";
import { useCalendarAreaId } from "./context/CalendarAreaContext";
import { useCalendarOptions } from "./hooks/useCalendarOptions";
import { useEmailCalendar } from "./hooks/useEmailCalendar";
import { useCalendarViewHandlers } from "./hooks/useEventHandlers";
import {
	useSearchEvents,
	useSearchEventsToCompare,
} from "./hooks/useSearchEvents";
import { Event } from "./models/Event";
import {
	confirmEvents,
	createDraftSnapshot,
	deleteEvent,
	resetDraft,
	restoreFromSnapshot,
	sendCalendarPublishNotification,
	updateEventDates,
} from "./repositories/CalendarRepository";
import { addDays, startOfMonth, startOfWeek } from "./utils/calendarUtils";
import { getDraftDivergenceSummary } from "./utils/draftDivergenceSummary";

const calculateCalendarDateBounds = (referenceDate: Date) => {
	const monthStart = startOfMonth(referenceDate);
	const calendarStart = startOfWeek(monthStart, 0);

	const calendarDays: Date[] = [];
	let day = calendarStart;
	for (let i = 0; i < 42; i++) {
		calendarDays.push(new Date(day));
		day = addDays(day, 1);
	}

	const serverTz = TimeService.getInstance().getServerTimeZone();

	const cStart = moment.tz(calendarDays[0], serverTz).toDate();
	cStart.setHours(0, 0, 0, 0);

	const cEnd = moment
		.tz(calendarDays[calendarDays.length - 1], serverTz)
		.toDate();
	cEnd.setHours(23, 59, 59, 999);

	return { start: cStart, end: cEnd };
};

const getInitialStateFromURL = (searchParams: URLSearchParams) => {
	const urlView = searchParams.get("view") as "calendar" | "list" | null;
	const urlDate = searchParams.get("date");
	const urlMode = searchParams.get("mode");

	const serverTz = TimeService.getInstance().getServerTimeZone();

	let displayMonthMoment = moment().tz(serverTz).date(1).startOf("day");

	if (urlDate) {
		const parsedMoment = moment.tz(urlDate, "YYYY-MM-DD", serverTz);
		if (parsedMoment.isValid()) {
			displayMonthMoment = parsedMoment.clone().date(1).startOf("day");
		}
	}

	const displayMonth = displayMonthMoment.toDate();

	const { start, end } = calculateCalendarDateBounds(displayMonth);

	return {
		view: urlView === "list" || urlView === "calendar" ? urlView : "list",
		displayMonth,
		start,
		end,
		isDraftMode: urlMode === "draft",
	};
};

export const CustomCalendar = () => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const defaultAreaId = useCalendarAreaId();
	const millLabel =
		Number(defaultAreaId) === MILL_1.id
			? "M1"
			: Number(defaultAreaId) === MILL_2.id
				? "M2"
				: null;
	const { canEditSchedule, canAddDownDay } = useCalendarPermissions();
	const { state: settingsState } = useUTLSettingsContext();
	const enableDraftSnapshots =
		get(settingsState.settings, "SCHEDULING.ENABLE_DRAFT_SNAPSHOTS", "0") ===
		"1";

	const calendarRef = useRef<FullCalendar | null>(null);

	const initialState = getInitialStateFromURL(searchParams);

	const [isDraftMode, setIsDraftMode] = useState(initialState.isDraftMode);
	// Ref to always get current isDraftMode value in callbacks
	const isDraftModeRef = useRef(isDraftMode);
	isDraftModeRef.current = isDraftMode;

	const [calendarTitle, setCalendarTitle] = useState<string>("");
	const [calendarView, setCalendarView] = useState<
		"dayGridMonth" | "timeGridWeek" | "timeGridDay"
	>("dayGridMonth");
	const [isViewingToday, setIsViewingToday] = useState(
		initialState.displayMonth.getMonth() === new Date().getMonth(),
	);
	const [displayMonth, setDisplayMonth] = useState<Date>(
		initialState.displayMonth,
	);
	const [start, setStart] = useState<Date>(initialState.start);
	const [end, setEnd] = useState<Date>(initialState.end);
	const [view, setView] = useState<"calendar" | "list">(initialState.view);

	const [error, setError] = useState("");

	const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
	const [eventModalOpen, setEventModalOpen] = useState(false);
	const [createEventModalOpen, setCreateEventModalOpen] = useState(false);
	const [subtitleNewEvent, setSubtitleNewEvent] = useState("");
	const [newEventDates, setNewEventDates] = useState<{
		start: Date;
		end: Date | null;
	} | null>(null);
	const [preserveNewEventDates, setPreserveNewEventDates] = useState(false);
	const [openConfirmModal, setOpenConfirmModal] = useState(false);
	const [openConfirmEventsDialog, setOpenConfirmEventsDialog] = useState(false);
	const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState<
		number | null
	>(null);
	const [showConfirmResetDraftModal, setShowConfirmResetDraftModal] =
		useState(false);
	const [showRestoreSnapshotModal, setShowRestoreSnapshotModal] =
		useState(false);

	const {
		data,
		isLoading,
		error: e,
		isError,
	} = useSearchEvents({
		start,
		end,
		stopRefetch: eventModalOpen || createEventModalOpen,
		isDraftMode,
	});

	const { data: compareData } = useSearchEventsToCompare({
		callSearch: canEditSchedule,
	});

	const draftDivergenceSummary = useMemo(
		() =>
			getDraftDivergenceSummary({
				mainEvents: compareData?.mainEvents ?? [],
				draftEvents: compareData?.draftEvents ?? [],
			}),
		[compareData],
	);

	const invalidateCalendarEventQueries = async () => {
		await Promise.all([
			queryClient.invalidateQueries({
				queryKey: ["event"],
			}),
			queryClient.invalidateQueries({
				queryKey: ["eventsToCompare"],
			}),
		]);
	};

	const remove = useMutation({
		mutationFn: (params: {
			eventId: number;
			deleteMode: "Both" | "Main" | "Draft";
		}) =>
			deleteEvent({
				eventId: params.eventId,
				deleteMode: params.deleteMode,
				defaultAreaId,
			}),
		meta: { successMessage: "The event was deleted successfully" },
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: ["event"],
				}),
				queryClient.invalidateQueries({
					queryKey: ["eventsToCompare"],
				}),
			]);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const updateDates = useMutation({
		mutationFn: (params: {
			eventId: number;
			newStartDate: Date;
			newEndDate: Date;
			isDraftMode: boolean;
		}) => updateEventDates({ ...params, defaultAreaId }),
		meta: { successMessage: "Event rescheduled successfully" },
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: ["event"],
				}),
				queryClient.invalidateQueries({
					queryKey: ["eventsToCompare"],
				}),
			]);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const resetDraftMutation = useMutation({
		mutationFn: () => resetDraft(defaultAreaId),
		onSuccess: async (result) => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: ["event"],
				}),
				queryClient.invalidateQueries({
					queryKey: ["eventsToCompare"],
				}),
			]);
			const msg = result
				? `Draft reset: ${result.EventsInserted ?? 0} inserted, ${
						result.EventsUpdated ?? 0
					} updated, ${result.EventsDeleted ?? 0} deleted`
				: "Draft reset successfully";
			enqueueSnackbar(msg, { variant: "success" });
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const confirmEventsMutation = useMutation({
		mutationFn: (confirmUntilEventId?: number | null) =>
			confirmEvents(defaultAreaId, confirmUntilEventId),
		onSuccess: async (result) => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: ["event"],
				}),
				queryClient.invalidateQueries({
					queryKey: ["eventsToCompare"],
				}),
			]);
			const msg = result
				? `Events published: ${result.EventsInserted} inserted, ${result.EventsUpdated} updated, ${result.EventsDeleted} deleted`
				: "Events published successfully";
			enqueueSnackbar(msg, { variant: "success" });
			setIsDraftMode(false);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const sendEmailsMutation = useMutation({
		mutationFn: (params: {
			emailRecipients: string;
			eventsPublished?: number;
			eventsCreated?: number;
			eventsUpdated?: number;
			eventsDeleted?: number;
			publisherNote?: string;
			addRecipientsToTopic?: boolean;
		}) =>
			sendCalendarPublishNotification({
				resource: "CALENDAR_PUBLISH",
				emailRecipients: params.emailRecipients,
				eventsPublished: params.eventsPublished,
				publisherNote: params.publisherNote,
				addRecipientsToTopic: params.addRecipientsToTopic,
				defaultAreaId,
			}),
		onSuccess: async (_result, params) => {
			if (params.addRecipientsToTopic) {
				await queryClient.invalidateQueries({ queryKey: ["emailRecipients"] });
				await queryClient.invalidateQueries({ queryKey: ["emailAddresses"] });
			}
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const emailCalendar = useEmailCalendar({
		defaultAreaId,
		onSuccess: () =>
			enqueueSnackbar("Calendar email queued successfully", {
				variant: "success",
			}),
		onError: (mutationError) => setError(mutationError.message),
	});

	const restoreSnapshotMutation = useMutation({
		mutationFn: (snapshotId: number) =>
			restoreFromSnapshot(snapshotId, defaultAreaId),
		onSuccess: async (result) => {
			await invalidateCalendarEventQueries();
			await queryClient.invalidateQueries({
				queryKey: ["draftSnapshots"],
			});
			setShowRestoreSnapshotModal(false);
			const msg = `Draft restored: ${result.EventsInserted} events restored (backup created as snapshot #${result.BackupSnapshotId})`;
			enqueueSnackbar(msg, { variant: "success" });
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const createSnapshotMutation = useMutation({
		mutationFn: () =>
			createDraftSnapshot(defaultAreaId, "MANUAL", "Manual snapshot"),
		onSuccess: async (result) => {
			if (result.Skipped) {
				const msg =
					result.EventCount === 0
						? "No events in draft to save"
						: "No changes to save - draft is identical to the last snapshot";
				enqueueSnackbar(msg, { variant: "info" });
			} else {
				await queryClient.invalidateQueries({
					queryKey: ["draftSnapshots"],
				});
				const msg = `Snapshot created: ${result.EventCount} events saved (Snapshot #${result.SnapshotId})`;
				enqueueSnackbar(msg, { variant: "success" });
			}
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const { registerConfig, showContextMenu } = useContextMenuMESF();
	const handlePrevious = () => {
		setDisplayMonth((prev) => {
			const newMonth = new Date(prev.getFullYear(), prev.getMonth() - 1, 1);
			const { start: newStart, end: newEnd } =
				calculateCalendarDateBounds(newMonth);
			setStart(newStart);
			setEnd(newEnd);
			setIsViewingToday(newMonth.getMonth() === new Date().getMonth());
			return newMonth;
		});
	};

	const handleNext = () => {
		setDisplayMonth((prev) => {
			const newMonth = new Date(prev.getFullYear(), prev.getMonth() + 1, 1);
			const { start: newStart, end: newEnd } =
				calculateCalendarDateBounds(newMonth);
			setStart(newStart);
			setEnd(newEnd);
			setIsViewingToday(newMonth.getMonth() === new Date().getMonth());
			return newMonth;
		});
	};

	const handleToday = () => {
		const serverTz = TimeService.getInstance().getServerTimeZone();
		const displayMonthDate = moment.tz(serverTz).date(1).toDate();
		const { start: newStart, end: newEnd } =
			calculateCalendarDateBounds(displayMonthDate);

		setDisplayMonth(displayMonthDate);
		setStart(newStart);
		setEnd(newEnd);
		setIsViewingToday(true);
	};

	const handleDateRangeSelect = (startDate: Date, endDate: Date) => {
		if (!canAddDownDay) return;
		setNewEventDates({
			start: startDate,
			end: endDate,
		});
		setCreateEventModalOpen(true);
	};

	const handleEventDrop = (
		eventId: number,
		newStartDate: Date,
		newEndDate: Date,
	): void => {
		if (!canEditSchedule) return;
		const originalEvent = data?.find((e) => e.EventId === eventId);
		const originalStart = originalEvent?.Start?.getTime();
		const originalEnd = originalEvent?.End?.getTime();

		if (
			newStartDate.getTime() === originalStart &&
			newEndDate.getTime() === originalEnd
		) {
			return;
		}

		updateDates.mutate({
			eventId,
			newStartDate,
			newEndDate,
			isDraftMode: isDraftModeRef.current,
		});
	};

	const { handleListViewClick, handleCalendarViewClick } =
		useCalendarViewHandlers({
			start,
			setStart,
			setEnd,
			setCalendarView,
			setView,
			setIsViewingToday,
			setCalendarTitle,
			calendarRef,
		});

	const { getMenuOptions } = useCalendarOptions({
		canEdit: canEditSchedule,
		canAdd: canAddDownDay,
		onNew: (
			clickedDate: Date,
			endDate?: Date | null,
			previousSection?: string,
			nextSection?: string,
		) => {
			if (endDate) {
				setNewEventDates({ start: clickedDate, end: endDate });
				setPreserveNewEventDates(true);
				setSubtitleNewEvent(
					previousSection || nextSection
						? `Creating event ${
								previousSection ? `after section '${previousSection}'` : ""
							}${previousSection && nextSection ? " and " : ""}${
								nextSection ? `before '${nextSection}'` : ""
							}`
						: "",
				);
				setCreateEventModalOpen(true);
				return;
			}

			const serverTz = TimeService.getInstance().getServerTimeZone();

			const year = clickedDate.getFullYear();
			const month = clickedDate.getMonth();
			const day = clickedDate.getDate();

			const startDate = moment
				.tz([year, month, day, 0, 0, 0], serverTz)
				.toDate();

			const nextDayStart = moment
				.tz([year, month, day, 0, 0, 0], serverTz)
				.add(1, "day")
				.toDate();

			setSubtitleNewEvent("");
			setNewEventDates({
				start: startDate,
				end: nextDayStart,
			});
			setPreserveNewEventDates(false);
			setCreateEventModalOpen(true);
		},
		onEdit: (eventId: number) => {
			if (eventId) {
				setSelectedEventId(eventId);
				setEventModalOpen(true);
			}
		},
		onDelete: (eventId: number) => {
			setShowConfirmDeleteModal(eventId);
		},
		openConfirmModal: (event: Event) => {
			setOpenConfirmModal(true);
			setSelectedEventId(event.EventId);
		},
	});

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [isError, e]);

	useEffect(() => {
		registerConfig({
			id: "calendarContext",
			getOptions: getMenuOptions,
		});
	}, []);

	const showContextMenuIfAny = (e: MouseEvent, data: any, configId: string) => {
		if (getMenuOptions(data).length === 0) {
			e.preventDefault();
			e.stopPropagation();
			return;
		}
		showContextMenu(e, data, configId);
	};

	useEffect(() => {
		const currentView = searchParams.get("view");
		const currentDate = searchParams.get("date");
		const currentMode = searchParams.get("mode");

		const dateStr = displayMonth.toLocaleDateString("en-CA", {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
		});

		const modeStr = isDraftMode ? "draft" : "published";

		if (
			currentView !== view ||
			currentDate !== dateStr ||
			currentMode !== modeStr
		) {
			const newParams = new URLSearchParams(searchParams);
			newParams.set("view", view);
			newParams.set("date", dateStr);
			newParams.set("mode", modeStr);
			navigate(`?${newParams.toString()}`, { replace: true });
		}

		setCalendarTitle(
			displayMonth.toLocaleDateString("en-US", {
				month: "long",
				year: "numeric",
			}),
		);
	}, [view, displayMonth, isDraftMode, navigate, searchParams]);

	const isSaving =
		remove.isPending ||
		updateDates.isPending ||
		resetDraftMutation.isPending ||
		confirmEventsMutation.isPending ||
		restoreSnapshotMutation.isPending ||
		createSnapshotMutation.isPending ||
		emailCalendar.isPending;

	return (
		<>
			<HelmetDexteel title={`${calendarTitle} - Calendar`} />
			<Box
				sx={{
					padding: (theme) => theme.spacing(2),
					backgroundColor: (theme) =>
						isDraftMode ? alpha(theme.palette.warning.main, 0.2) : pageBgColor,
					transition: "background-color 0.3s ease-in-out",
					minHeight: masterDetailsMinHeight,
				}}
			>
				<Box
					style={{
						display: "flex",
						justifyContent: "space-between",
						paddingBottom: 10,
					}}
				>
					<Box style={{ display: "flex", gap: 10 }}>
						<ButtonGroup>
							<Button
								variant="outlined"
								onClick={() => handlePrevious()}
								disabled={isSaving}
								data-test-id="previous-month-button"
							>
								Previous
							</Button>
							<Button
								variant={isViewingToday ? "contained" : "outlined"}
								onClick={() => handleToday()}
								disabled={isSaving}
								color="primary"
								data-test-id="today-month-button"
							>
								Today
							</Button>
							<Button
								variant="outlined"
								onClick={() => handleNext()}
								disabled={isSaving}
								data-test-id="next-month-button"
							>
								Next
							</Button>
						</ButtonGroup>
						<ButtonGroup>
							<Button
								variant={view === "calendar" ? "contained" : "outlined"}
								startIcon={<CalendarToday />}
								onClick={handleCalendarViewClick}
								color="primary"
								disabled={isSaving}
							>
								Calendar
							</Button>
							<Button
								variant={view === "list" ? "contained" : "outlined"}
								startIcon={<ListIcon />}
								onClick={handleListViewClick}
								color="primary"
								disabled={isSaving}
							>
								List
							</Button>
						</ButtonGroup>
					</Box>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
						}}
					>
						{millLabel && (
							<Typography
								variant="caption"
								sx={{
									color: "#666",
									fontWeight: 500,
									lineHeight: 1,
									letterSpacing: 0.5,
								}}
							>
								Calendar {millLabel}
							</Typography>
						)}
						<Typography
							style={{ fontSize: "1.25rem", fontWeight: 600 }}
							data-test-id="calendar-header"
						>
							{calendarTitle || "Calendar"}
						</Typography>
					</Box>
					<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
						{draftDivergenceSummary.total > 0 && (
							<Tooltip
								title={
									<Box>
										<Typography variant="subtitle2">
											Pending Draft Changes
										</Typography>
										<Typography variant="body2">
											Created: {draftDivergenceSummary.created}
										</Typography>
										<Typography variant="body2">
											Deleted: {draftDivergenceSummary.deleted}
										</Typography>
										<Typography variant="body2">
											Updated: {draftDivergenceSummary.updated}
										</Typography>
										<Typography variant="body2">
											Moved: {draftDivergenceSummary.moved}
										</Typography>
									</Box>
								}
								arrow
							>
								<Chip
									label={draftDivergenceSummary.total}
									color="warning"
									size="small"
									variant={isDraftMode ? "filled" : "outlined"}
									sx={{ fontWeight: 700 }}
								/>
							</Tooltip>
						)}
						<ButtonGroup>
							<Button
								variant={isDraftMode ? "contained" : "outlined"}
								onClick={() => setIsDraftMode(true)}
								color="primary"
								disabled={isSaving || !canEditSchedule}
								data-test-id="draft-mode-button"
							>
								Draft
							</Button>
							<Button
								variant={isDraftMode ? "outlined" : "contained"}
								onClick={() => setIsDraftMode(false)}
								color="primary"
								disabled={isSaving || !canEditSchedule}
								data-test-id="published-mode-button"
							>
								Published
							</Button>
						</ButtonGroup>
						<ButtonGroup>
							{enableDraftSnapshots && (
								<>
									<Button
										variant="outlined"
										startIcon={<Save />}
										onClick={() => createSnapshotMutation.mutate()}
										color="primary"
										disabled={!isDraftMode || isSaving || !canEditSchedule}
										title="Save current draft state as a snapshot"
									>
										Save
									</Button>
									<Button
										variant="outlined"
										startIcon={<History />}
										onClick={() => setShowRestoreSnapshotModal(true)}
										color="info"
										disabled={!isDraftMode || isSaving || !canEditSchedule}
										title="Restore draft from a previous snapshot"
									>
										Restore
									</Button>
								</>
							)}
							<Button
								variant="outlined"
								startIcon={<Restore />}
								onClick={() => setShowConfirmResetDraftModal(true)}
								color="warning"
								disabled={!isDraftMode || isSaving || !canEditSchedule}
								title="Reset draft to match published state"
								data-test-id="reset-draft-button"
							>
								Reset
							</Button>
							<Button
								variant="contained"
								startIcon={<Publish />}
								onClick={() => setOpenConfirmEventsDialog(true)}
								color="success"
								disabled={!isDraftMode || isSaving || !canEditSchedule}
								title="Publish draft changes to main"
								data-test-id="publish-draft-button"
							>
								Publish
							</Button>
							{canEditSchedule && (
								<Button
									variant="outlined"
									startIcon={<Email />}
									onClick={emailCalendar.open}
									disabled={isSaving || isDraftMode}
									data-test-id="email-calendar-button"
								>
									Email
								</Button>
							)}
						</ButtonGroup>
					</Box>
				</Box>

				{view === "calendar" ? (
					<EnhancedMonthCalendar
						events={data || []}
						initialDate={displayMonth}
						onEventClick={(event) => {
							if (event?.EventId) {
								setSelectedEventId(event.EventId);
								setEventModalOpen(true);
							}
						}}
						onDateRangeSelect={handleDateRangeSelect}
						onEventDrop={handleEventDrop}
						selectable={canAddDownDay}
						editable={canEditSchedule}
						showContextMenu={showContextMenuIfAny}
					/>
				) : (
					<Box component={Paper} style={{ padding: 16 }}>
						<CustomEventsTable
							month={new Date((start.getTime() + end.getTime()) / 2).getMonth()}
							year={new Date(
								(start.getTime() + end.getTime()) / 2,
							).getFullYear()}
							onEventClick={(eventId) => {
								setSelectedEventId(eventId);
								setEventModalOpen(true);
							}}
							onDelete={(eventId: number) => {
								setShowConfirmDeleteModal(eventId);
							}}
							onSuccess={async (message?: string) => {
								enqueueSnackbar(message ?? "Event modified successfully", {
									variant: "success",
								});
								await invalidateCalendarEventQueries();
							}}
							refetch={() => {
								void invalidateCalendarEventQueries();
							}}
							setOpenConfirmModal={(id: number) => {
								setOpenConfirmModal(true);
								setSelectedEventId(id);
							}}
							isDraftMode={isDraftMode}
						/>
					</Box>
				)}
			</Box>

			<ConfirmEventModal
				selectedEventId={selectedEventId}
				monthStart={displayMonth}
				open={openConfirmModal}
				onHide={async (update: boolean) => {
					setOpenConfirmModal(false);
					if (update) {
						enqueueSnackbar("Event published successfully", {
							variant: "success",
						});
						await invalidateCalendarEventQueries();
					}
				}}
				isDraftMode={isDraftMode}
			/>
			<EventModal
				open={eventModalOpen}
				selectedEventId={selectedEventId}
				monthStart={displayMonth}
				onHide={async (success: boolean) => {
					setEventModalOpen(false);

					if (success) {
						enqueueSnackbar("Event modified successfully", {
							variant: "success",
						});
						await invalidateCalendarEventQueries();
					}
				}}
				isDraftMode={isDraftMode}
				disabled={!canEditSchedule}
			/>
			<NewEventModal
				events={data || []}
				open={createEventModalOpen}
				subtitle={subtitleNewEvent}
				onHide={async (success: boolean) => {
					setCreateEventModalOpen(false);
					setNewEventDates(null);
					setPreserveNewEventDates(false);
					setSubtitleNewEvent("");

					if (success) {
						enqueueSnackbar("Event created successfully", {
							variant: "success",
						});
						await invalidateCalendarEventQueries();
					}
				}}
				newEventDates={newEventDates}
				isDraftMode={isDraftMode}
				restrictToEventType={canEditSchedule ? undefined : "D"}
				preserveEventDates={preserveNewEventDates}
			/>

			<ConfirmDraftEventsModal
				open={openConfirmEventsDialog}
				start={start}
				end={end}
				externalLoading={confirmEventsMutation.isPending}
				onHide={() => setOpenConfirmEventsDialog(false)}
				onConfirm={async (
					confirmUntilEventId,
					selectedEmails,
					eventsPublished,
					publisherNote,
					addRecipientsToTopic,
				) => {
					const result =
						await confirmEventsMutation.mutateAsync(confirmUntilEventId);
					if (selectedEmails && result) {
						const eventsCreated = result.EventsInserted ?? 0;
						const eventsUpdated = result.EventsUpdated ?? 0;
						const eventsDeleted = result.EventsDeleted ?? 0;
						sendEmailsMutation.mutate({
							emailRecipients: selectedEmails,
							eventsPublished,
							eventsCreated,
							eventsUpdated,
							eventsDeleted,
							publisherNote,
							addRecipientsToTopic,
						});
					}
				}}
			/>

			<EmailCalendarModal
				open={emailCalendar.isOpen}
				loading={emailCalendar.isPending}
				onClose={emailCalendar.close}
				onConfirm={emailCalendar.send}
			/>

			<ConfirmDeleteEventModal
				open={!!showConfirmDeleteModal}
				externalLoading={confirmEventsMutation.isPending}
				onHide={() => setShowConfirmDeleteModal(null)}
				onDelete={(_, deleteMode: "Both" | "Main" | "Draft") => {
					// Delete by the id the modal was opened for: `event` comes from the
					// events query, which a refetch (another user's change) can leave
					// without this event, and then the delete was sent with no EventId
					remove.mutate({ eventId: showConfirmDeleteModal!, deleteMode });
					setShowConfirmDeleteModal(null);
				}}
				event={data?.find((e) => e.EventId === showConfirmDeleteModal) ?? null}
				calledFrom={isDraftMode ? "Draft" : "Main"}
			/>

			<ConfirmationDialogRaw
				title="Reset Draft"
				message={
					"Are you sure you want to reset draft, and paste 'Published' into 'Draft'?\n\nA snapshot of the current draft will be created automatically before reset."
				}
				open={showConfirmResetDraftModal}
				onClose={(value?: string) => {
					if (value == "OK") {
						resetDraftMutation.mutate();
					}
					setShowConfirmResetDraftModal(false);
				}}
			/>

			<RestoreDraftSnapshotModal
				open={showRestoreSnapshotModal}
				externalLoading={restoreSnapshotMutation.isPending}
				onHide={() => setShowRestoreSnapshotModal(false)}
				onRestore={(snapshotId) => {
					restoreSnapshotMutation.mutate(snapshotId);
				}}
			/>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
